import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const safe = (v) => String(v ?? '').trim();
const lower = (v) => safe(v).toLowerCase();
const fmtDateTime = (s) => {
  const v = safe(s);
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
};

const COLORS = ['#6d28d9', '#2563eb', '#059669', '#f59e0b', '#ef4444', '#14b8a6', '#64748b'];

const QUESTION_DEFS = [
  { key: 'rating_week_1_10', label: 'Week rating (1���10)', type: 'rating10' },
  { key: 'hope_to_come_back', label: 'Hope to come back next year?', type: 'yesno' },

  { key: 'favorite_activity', label: 'Favorite activity', type: 'text' },
  { key: 'least_favorite_activity', label: 'Least favorite activity', type: 'text' },
  { key: 'activity_suggestions', label: 'Activity suggestions', type: 'text' },
  { key: 'counselor_volunteer_changes', label: 'Counselor/volunteer improvements', type: 'text' },
  { key: 'speaker_recommendations', label: 'Speaker recommendations', type: 'text' },
  { key: 'adults_feedback', label: 'Adults feedback', type: 'text' },
  { key: 'parents_small_children_feedback', label: 'Parents with small children feedback', type: 'text' },
  { key: 'evening_evangelist_request', label: 'Evening evangelist request', type: 'text' },

  { key: 'evening_music_preference_1_5', label: 'Music preference (1–5)', type: 'rating5' },

  // NOTE: We keep both keys here so they still appear in Question/Individual views,
  // but in Summary we combine them into one cross-tab.
  { key: 'morning_session_group', label: 'Morning session group', type: 'choice' },
  { key: 'morning_session_enjoyment', label: 'Morning session enjoyment', type: 'choice' },

  { key: 'morning_session_explain', label: 'Explain morning answer', type: 'text' },

  { key: 'meals_suggestions', label: 'Meals suggestions', type: 'text' },
  { key: 'sleep_location_accommodations', label: 'Sleep / accommodations', type: 'text' },
  { key: 'suggestions_2026_anything', label: 'Suggestions for 2026', type: 'text' },

  { key: 'invite_more_people', label: 'Invite more people?', type: 'choice' },

  { key: 'leadership_interest_roles', label: 'Leadership roles interested in', type: 'choiceMulti' },
  { key: 'final_comments', label: 'Final comments', type: 'text' }
];

const findValue = (row, keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && safe(v) !== '') return v;
  }
  return '';
};

// include email/phone if provided (supports multiple possible column names)
const getEmail = (r) => findValue(r, ['email', 'contact_email', 'leadership_contact_email']);
const getPhone = (r) => findValue(r, ['phone', 'contact_phone', 'leadership_contact_phone']);
const getName = (r) => findValue(r, ['name', 'contact_name', 'leadership_contact_name']);

const parseRoles = (v) =>
  safe(v)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const countBy = (list, getter) => {
  const m = new Map();
  for (const item of list) {
    const k = safe(getter(item));
    if (!k) continue;
    m.set(k, (m.get(k) || 0) + 1);
  }
  return [...m.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

const countByNumericRange = (list, getter, min, max) => {
  const counts = {};
  for (let i = min; i <= max; i++) counts[i] = 0;

  for (const item of list) {
    const raw = safe(getter(item)).replace(/[^\d]/g, '');
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    if (n < min || n > max) continue;
    counts[n] += 1;
  }

  return Object.keys(counts).map((k) => ({ name: k, value: counts[k] }));
};

const buildCountsForQuestion = (list, q) => {
  const key = q.key;
  const type = q.type;

  if (type === 'rating10') return countByNumericRange(list, (r) => r[key], 1, 10);
  if (type === 'rating5') return countByNumericRange(list, (r) => r[key], 1, 5);

  if (type === 'yesno') {
    return countBy(list, (r) => {
      const s = lower(r[key]);
      if (s.startsWith('y')) return 'Yes';
      if (s.startsWith('n')) return 'No';
      return '';
    });
  }

  if (type === 'choice') return countBy(list, (r) => r[key]);

  if (type === 'choiceMulti') {
    const m = new Map();
    for (const r of list) {
      for (const v of parseRoles(r[key])) m.set(v, (m.get(v) || 0) + 1);
    }
    return [...m.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }

  return [];
};

const getTextAnswersForQuestion = (list, q) => list.map((r) => safe(r[q.key])).filter(Boolean);

function Card({ title, subtitle, children, right }) {
  return (
    <div style={{ border: '1px solid #eee', borderRadius: 12, background: '#fff', padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div>
          <div style={{ fontWeight: 900 }}>{title}</div>
          {subtitle ? <div className="muted" style={{ marginTop: 2, fontSize: 12 }}>{subtitle}</div> : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

function ScrollList({ items }) {
  return (
    <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 10 }}>
      {items.length ? (
        items.map((t, idx) => (
          <div key={idx} style={{ padding: '10px 12px', borderTop: idx ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{t}</div>
          </div>
        ))
      ) : (
        <div className="muted" style={{ padding: '10px 12px' }}>No responses yet.</div>
      )}
    </div>
  );
}

function PrintBlock({ row }) {
  const Field = ({ label, value }) => {
    const v = safe(value);
    if (!v) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{label}</div>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontWeight: 700 }}>{v}</div>
      </div>
    );
  };

  return (
    <div className="print-area" style={{ marginTop: 12, background: '#fff' }}>
      <h2 style={{ margin: '0 0 6px 0' }}>Camp Fusion Survey Response</h2>
      <div className="muted" style={{ marginBottom: 14 }}>
        Submitted: {fmtDateTime(row?.submitted_at)}
        {safe(getEmail(row)) ? ` • ${safe(getEmail(row))}` : ''}
        {safe(getPhone(row)) ? ` • ${safe(getPhone(row))}` : ''}
      </div>

      <h3 style={{ margin: '16px 0 8px 0' }}>Overall</h3>
      <Field label="Week rating (1–10)" value={row?.rating_week_1_10} />
      <Field label="Hope to come back next year?" value={row?.hope_to_come_back} />
      <Field label="Favorite activity" value={row?.favorite_activity} />
      <Field label="Least favorite activity" value={row?.least_favorite_activity} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Activities</h3>
      <Field label="Activity suggestions" value={row?.activity_suggestions} />
      <Field label="Counselor/volunteer improvements" value={row?.counselor_volunteer_changes} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Speakers / Services</h3>
      <Field label="Speaker recommendations" value={row?.speaker_recommendations} />
      <Field label="Evening evangelist request" value={row?.evening_evangelist_request} />
      <Field label="Music preference (1–5)" value={row?.evening_music_preference_1_5} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Morning session</h3>
      <Field label="Group" value={row?.morning_session_group} />
      <Field label="Enjoyment" value={row?.morning_session_enjoyment} />
      <Field label="Explain previous answer" value={row?.morning_session_explain} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Adults / Parents</h3>
      <Field label="Adults feedback" value={row?.adults_feedback} />
      <Field label="Parents with small children feedback" value={row?.parents_small_children_feedback} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Meals / Lodging</h3>
      <Field label="Meals suggestions" value={row?.meals_suggestions} />
      <Field label="Sleep / accommodations" value={row?.sleep_location_accommodations} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Next year</h3>
      <Field label="Suggestions for 2026" value={row?.suggestions_2026_anything} />
      <Field label="Invite more people" value={row?.invite_more_people} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Leadership</h3>
      <Field label="Interested roles" value={row?.leadership_interest_roles} />
      <Field label="Name" value={getName(row)} />
      <Field label="Email" value={getEmail(row)} />
      <Field label="Phone" value={getPhone(row)} />

      <h3 style={{ margin: '16px 0 8px 0' }}>Final</h3>
      <Field label="Final comments" value={row?.final_comments} />
    </div>
  );
}

export default function AdminSurvey({ token }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [surveyData, setSurveyData] = useState({ headers: [], rows: [] });

  // Top-level view: Summary | Question | Individual
  const [view, setView] = useState('summary'); // summary | question | individual

  // filters
  const [query, setQuery] = useState('');
  const [leadershipOnly, setLeadershipOnly] = useState(false);

  // summary accordion open item
  const [openSummaryKey, setOpenSummaryKey] = useState('rating_week_1_10');

  // question view selection
  const [selectedQuestionKey, setSelectedQuestionKey] = useState('rating_week_1_10');

  // individual selection
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // print modal (still used for Individual -> Print/View)
  const [printModal, setPrintModal] = useState({ open: false, row: null });

  // PDF/print mode (renders full PDF DOM, triggers print dialog, then unmounts)
  const [pdfMode, setPdfMode] = useState(false);

  useEffect(() => {
    if (!pdfMode) return;

    const t = setTimeout(() => {
      window.print();
      setPdfMode(false);
    }, 250);

    return () => clearTimeout(t);
  }, [pdfMode]);

  const fetchSurvey = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-get-survey', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }

      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);

      setSurveyData({
        headers: Array.isArray(json.headers) ? json.headers : [],
        rows: Array.isArray(json.rows) ? json.rows : []
      });
    } catch (e) {
      setError(String(e?.message || e));
      setSurveyData({ headers: [], rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const rows = useMemo(() => (Array.isArray(surveyData?.rows) ? surveyData.rows : []), [surveyData]);

  const filteredRows = useMemo(() => {
    const q = lower(query);
    let list = rows;

    if (leadershipOnly) {
      list = list.filter((r) => safe(r.leadership_interest_roles));
    }

    if (q) {
      list = list.filter((r) => {
        const hay = [
          r.submitted_at,
          getName(r),
          getEmail(r),
          getPhone(r),

          r.rating_week_1_10,
          r.hope_to_come_back,

          r.favorite_activity,
          r.least_favorite_activity,
          r.activity_suggestions,
          r.counselor_volunteer_changes,
          r.speaker_recommendations,
          r.adults_feedback,
          r.parents_small_children_feedback,
          r.evening_evangelist_request,
          r.evening_music_preference_1_5,
          r.morning_session_group,
          r.morning_session_enjoyment,
          r.morning_session_explain,
          r.meals_suggestions,
          r.sleep_location_accommodations,
          r.suggestions_2026_anything,
          r.invite_more_people,
          r.leadership_interest_roles,
          r.final_comments
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return hay.includes(q);
      });
    }

    // newest first
    list = [...list].sort((a, b) => {
      const at = Date.parse(a.submitted_at || '') || 0;
      const bt = Date.parse(b.submitted_at || '') || 0;
      return bt - at;
    });

    return list;
  }, [rows, query, leadershipOnly]);

  // Cross-tab: Morning group x enjoyment (for Summary + PDF)
  const morningCrosstab = useMemo(() => {
    const rowKey = 'morning_session_group';
    const colKey = 'morning_session_enjoyment';

    const rowSet = new Set();
    const colSet = new Set();
    const matrix = new Map(); // rowVal -> Map(colVal -> count)

    for (const r of filteredRows) {
      const rv = safe(r?.[rowKey]);
      const cv = safe(r?.[colKey]);
      if (!rv || !cv) continue;

      rowSet.add(rv);
      colSet.add(cv);

      if (!matrix.has(rv)) matrix.set(rv, new Map());
      const m = matrix.get(rv);
      m.set(cv, (m.get(cv) || 0) + 1);
    }

    const preferredRows = ['VBS', 'Middle School', 'High School', 'College', 'Young Adults', 'Adults'];
    const rowsSorted = [...rowSet].sort((a, b) => {
      const ai = preferredRows.indexOf(a);
      const bi = preferredRows.indexOf(b);
      if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      return a.localeCompare(b);
    });

    const colsSorted = [...colSet].sort((a, b) => a.localeCompare(b));

    return { rows: rowsSorted, cols: colsSorted, matrix };
  }, [filteredRows]);

  // If we haven't selected an individual yet, default to newest
  useEffect(() => {
    if (view !== 'individual') return;
    if (selectedRowIndex != null) return;
    if (!filteredRows.length) return;
    setSelectedRowIndex(filteredRows[0]._rowIndex ?? 2);
  }, [view, selectedRowIndex, filteredRows]);

  const selectedRow = useMemo(() => {
    if (selectedRowIndex == null) return null;
    return filteredRows.find((r) => Number(r._rowIndex) === Number(selectedRowIndex)) || null;
  }, [filteredRows, selectedRowIndex]);

  // Question view
  const selectedQuestion = QUESTION_DEFS.find((q) => q.key === selectedQuestionKey) || QUESTION_DEFS[0];

  const questionCounts = useMemo(
    () => buildCountsForQuestion(filteredRows, selectedQuestion),
    [filteredRows, selectedQuestion]
  );

  const questionTextAnswers = useMemo(() => {
    if (selectedQuestion.type !== 'text') return [];
    return getTextAnswersForQuestion(filteredRows, selectedQuestion);
  }, [filteredRows, selectedQuestion]);

  return (
    <>
      {/* PDF-only DOM (printed via CSS rules that only show .pdf-document) */}
      {pdfMode ? (
        <div className="pdf-document">
          <h1 style={{ margin: 0 }}>Survey Responses (PDF)</h1>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            Generated: {new Date().toLocaleString()} • {filteredRows.length} response{filteredRows.length === 1 ? '' : 's'}
            {leadershipOnly ? ' • Leadership only' : ''}
            {query ? ` • Search: "${query}"` : ''}
          </div>

          <h2 style={{ marginTop: 18 }}>Who has responded?</h2>
          <ul style={{ marginTop: 8 }}>
            {filteredRows.map((r, i) => {
              const e = safe(getEmail(r));
              const p = safe(getPhone(r));
              const n = safe(getName(r));
              const id = [n, e, p].filter(Boolean).join(' • ');
              return <li key={r._rowIndex || i}>{id || `Response at ${fmtDateTime(r.submitted_at)}`}</li>;
            })}
          </ul>

          <h2 style={{ marginTop: 18 }}>Morning session (Group × Enjoyment)</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead>
              <tr>
                <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left', padding: 6 }}>Group</th>
                {morningCrosstab.cols.map((c) => (
                  <th key={c} style={{ borderBottom: '1px solid #ddd', textAlign: 'left', padding: 6 }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {morningCrosstab.rows.map((rv) => {
                const rowMap = morningCrosstab.matrix.get(rv) || new Map();
                return (
                  <tr key={rv}>
                    <td style={{ borderBottom: '1px solid #eee', padding: 6, fontWeight: 700 }}>{rv}</td>
                    {morningCrosstab.cols.map((c) => (
                      <td key={c} style={{ borderBottom: '1px solid #eee', padding: 6 }}>
                        {rowMap.get(c) || 0}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <h2 style={{ marginTop: 18 }}>Question summaries</h2>

          {QUESTION_DEFS.map((q) => {
            // Skip because we already include a combined cross-tab
            if (q.key === 'morning_session_group' || q.key === 'morning_session_enjoyment') return null;

            const answered =
              q.type === 'text'
                ? filteredRows.reduce((n, r) => (safe(r?.[q.key]) ? n + 1 : n), 0)
                : buildCountsForQuestion(filteredRows, q).reduce((s, x) => s + (x.value || 0), 0);

            if (q.type === 'text') {
              const answers = getTextAnswersForQuestion(filteredRows, q).slice(0, 200);

              return (
                <div key={q.key} style={{ marginTop: 14, pageBreakInside: 'avoid' }}>
                  <h3 style={{ margin: '0 0 6px 0' }}>{q.label}</h3>
                  <div style={{ fontSize: 12, color: '#666' }}>{answered} answered</div>

                  {answers.length ? (
                    <ul style={{ marginTop: 8 }}>
                      {answers.map((a, idx) => (
                        <li key={idx} style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}>{a}</li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ marginTop: 8, color: '#666' }}>No responses.</div>
                  )}

                  {answered > 200 ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                      Showing first 200 responses.
                    </div>
                  ) : null}
                </div>
              );
            }

            const counts = buildCountsForQuestion(filteredRows, q);

            return (
              <div key={q.key} style={{ marginTop: 14, pageBreakInside: 'avoid' }}>
                <h3 style={{ margin: '0 0 6px 0' }}>{q.label}</h3>
                <div style={{ fontSize: 12, color: '#666' }}>{answered} answered</div>

                {counts.length ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
                    <thead>
                      <tr>
                        <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left', padding: 6 }}>Answer</th>
                        <th style={{ borderBottom: '1px solid #ddd', textAlign: 'left', padding: 6, width: 90 }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {counts.map((c) => (
                        <tr key={c.name}>
                          <td style={{ borderBottom: '1px solid #eee', padding: 6 }}>{c.name}</td>
                          <td style={{ borderBottom: '1px solid #eee', padding: 6 }}>{c.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ marginTop: 8, color: '#666' }}>No responses.</div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', marginTop: 14 }}>
        <div>
          <h2 style={{ margin: 0 }}>Survey Responses</h2>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {filteredRows.length} response{filteredRows.length === 1 ? '' : 's'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${view === 'summary' ? 'primary' : ''}`} type="button" onClick={() => setView('summary')}>Summary</button>
          <button className={`btn ${view === 'question' ? 'primary' : ''}`} type="button" onClick={() => setView('question')}>Question</button>
          <button className={`btn ${view === 'individual' ? 'primary' : ''}`} type="button" onClick={() => setView('individual')}>Individual</button>

          <button className="btn" type="button" onClick={fetchSurvey} disabled={loading}>Refresh</button>
        </div>
      </div>

      <div className="admin-actions" style={{ marginTop: 10 }}>
        <input
          type="search"
          placeholder="Search (name/email/phone/answers)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginLeft: 0, minWidth: 260 }}
        />

        <label style={{ marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={leadershipOnly}
            onChange={(e) => setLeadershipOnly(e.target.checked)}
          />
          Leadership only
        </label>

        <button className="btn" type="button" style={{ marginLeft: 12 }} onClick={() => setPdfMode(true)}>
          Download PDF
        </button>
      </div>

      {loading ? <div>Loading…</div> : null}
      {error ? <div className="error">{error}</div> : null}

      {/* SUMMARY (accordion) */}
      {view === 'summary' && (
        <div className="print-summary" style={{ marginTop: 12, display: 'grid', gap: 12 }}>
          <Card title="Who has responded?" subtitle="Email / phone if provided">
            <ScrollList
              items={filteredRows
                .map((r) => {
                  const e = safe(getEmail(r));
                  const p = safe(getPhone(r));
                  const n = safe(getName(r));
                  const id = [n, e, p].filter(Boolean).join(' • ');
                  return id || `Response at ${fmtDateTime(r.submitted_at)}`;
                })
                .filter(Boolean)
                .slice(0, 200)}
            />
          </Card>

          <div style={{ border: '1px solid #eee', borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
            {QUESTION_DEFS.map((q, idx) => {
              const isOpen = openSummaryKey === q.key;

              const answeredCount =
                q.type === 'text'
                  ? filteredRows.reduce((n, r) => (safe(r[q.key]) ? n + 1 : n), 0)
                  : buildCountsForQuestion(filteredRows, q).reduce((s, x) => s + (x.value || 0), 0);

              // Combine Morning session group + enjoyment into one Summary item
              if (q.key === 'morning_session_group') {
                const combinedKey = 'morning_session_group__combined';
                const combinedOpen = openSummaryKey === combinedKey;

                const totalAnswered = filteredRows.reduce((n, r) => {
                  return safe(r.morning_session_group) && safe(r.morning_session_enjoyment) ? n + 1 : n;
                }, 0);

                return (
                  <div key={combinedKey} style={{ borderTop: idx ? '1px solid #f1f5f9' : 'none' }}>
                    <button
                      type="button"
                      onClick={() => setOpenSummaryKey((cur) => (cur === combinedKey ? '' : combinedKey))}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: combinedOpen ? '#f8fafc' : '#fff',
                        border: 'none',
                        padding: '12px 14px',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: '#64748b', width: 18 }}>
                            {combinedOpen ? '▾' : '▸'}
                          </div>
                          <div style={{ fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Morning session (Group × Enjoyment)
                          </div>
                        </div>

                        <div className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {totalAnswered} answered
                        </div>
                      </div>
                    </button>

                    {combinedOpen ? (
                      <div style={{ padding: '0 14px 14px 14px' }}>
                        <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 10 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                            <thead>
                              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                                <th style={{ padding: '10px 12px', position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                                  Group
                                </th>
                                {morningCrosstab.cols.map((c) => (
                                  <th key={c} style={{ padding: '10px 12px' }}>{c}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {morningCrosstab.rows.map((rv) => {
                                const rowMap = morningCrosstab.matrix.get(rv) || new Map();
                                const rowTotal = morningCrosstab.cols.reduce((s, c) => s + (rowMap.get(c) || 0), 0);

                                return (
                                  <tr key={rv} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '10px 12px', fontWeight: 900, position: 'sticky', left: 0, background: '#fff' }}>
                                      {rv} <span className="muted" style={{ fontWeight: 600 }}>({rowTotal})</span>
                                    </td>
                                    {morningCrosstab.cols.map((c) => {
                                      const v = rowMap.get(c) || 0;
                                      return (
                                        <td key={c} style={{ padding: '10px 12px' }}>
                                          {v ? <strong>{v}</strong> : <span className="muted">0</span>}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                          This shows how each morning group rated their session.
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              }

              // Skip the separate enjoyment question in Summary (included in the cross-tab above)
              if (q.key === 'morning_session_enjoyment') return null;

              return (
                <div key={q.key} style={{ borderTop: idx ? '1px solid #f1f5f9' : 'none' }}>
                  <button
                    type="button"
                    onClick={() => setOpenSummaryKey((cur) => (cur === q.key ? '' : q.key))}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: isOpen ? '#f8fafc' : '#fff',
                      border: 'none',
                      padding: '12px 14px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#64748b', width: 18 }}>
                          {isOpen ? '▾' : '▸'}
                        </div>
                        <div style={{ fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {q.label}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {answeredCount} answered
                        </div>

                        <button
                          className="btn"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedQuestionKey(q.key);
                            setView('question');
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </button>

                  {isOpen ? (
                    <div style={{ padding: '0 14px 14px 14px' }}>
                      {q.type === 'text' ? (
                        <ScrollList items={getTextAnswersForQuestion(filteredRows, q).slice(0, 200)} />
                      ) : (
                        (() => {
                          const counts = buildCountsForQuestion(filteredRows, q);
                          const isPie = q.type === 'yesno';

                          return (
                            <div style={{ height: 320, marginTop: 8 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                {isPie ? (
                                  <PieChart>
                                    <Pie data={counts} dataKey="value" nameKey="name" outerRadius={90} label>
                                      {counts.map((_, i) => (
                                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                  </PieChart>
                                ) : (
                                  <BarChart data={counts}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" interval={0} angle={-18} height={90} textAnchor="end" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#6d28d9" />
                                  </BarChart>
                                )}
                              </ResponsiveContainer>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* QUESTION */}
      {view === 'question' && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: 12, alignItems: 'start' }}>
          <div style={{ border: '1px solid #eee', borderRadius: 12, background: '#fff', padding: 12 }}>
            <div style={{ fontWeight: 900 }}>Select question</div>
            <div style={{ marginTop: 10 }}>
              <select
                value={selectedQuestionKey}
                onChange={(e) => setSelectedQuestionKey(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd', background: '#fff' }}
              >
                {QUESTION_DEFS.map((q) => (
                  <option key={q.key} value={q.key}>{q.label}</option>
                ))}
              </select>
            </div>

            <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
              Tip: use the search box above to search within answers.
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            <Card title={selectedQuestion.label} subtitle={`${filteredRows.length} total responses (after filters)`}>
              {selectedQuestion.type === 'text' ? (
                <ScrollList items={questionTextAnswers.slice(0, 200)} />
              ) : selectedQuestion.type === 'yesno' ? (
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={questionCounts} dataKey="value" nameKey="name" outerRadius={90} label>
                        {questionCounts.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={questionCounts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" interval={0} angle={-18} height={90} textAnchor="end" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#6d28d9" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* INDIVIDUAL */}
      {view === 'individual' && (
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'minmax(260px, 360px) 1fr', gap: 12, alignItems: 'start' }}>
          <div style={{ border: '1px solid #eee', borderRadius: 12, background: '#fff', padding: 12 }}>
            <div style={{ fontWeight: 900 }}>Responses</div>
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>Newest first</div>

            <div style={{ marginTop: 10, maxHeight: 520, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 10 }}>
              {filteredRows.length ? filteredRows.map((r) => {
                const idx = Number(r._rowIndex ?? 0);
                const active = Number(selectedRowIndex) === idx;
                const idLine = [safe(getName(r)), safe(getEmail(r)), safe(getPhone(r))].filter(Boolean).join(' • ');
                const title = idLine || `Response at ${fmtDateTime(r.submitted_at)}`;
                const subtitle = safe(r.leadership_interest_roles) ? 'Leadership: Yes' : '';

                return (
                  <button
                    key={idx || Math.random().toString(16)}
                    type="button"
                    className="btn"
                    onClick={() => setSelectedRowIndex(idx)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 0,
                      border: 'none',
                      borderTop: '1px solid #f3f4f6',
                      background: active ? '#f5f3ff' : '#fff',
                      padding: '10px 12px'
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{title}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {fmtDateTime(r.submitted_at)}{subtitle ? ` • ${subtitle}` : ''}
                    </div>
                  </button>
                );
              }) : (
                <div className="muted" style={{ padding: '10px 12px' }}>No responses yet.</div>
              )}
            </div>
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: 12, background: '#fff', padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 900 }}>Individual response</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {selectedRow ? fmtDateTime(selectedRow.submitted_at) : '—'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" type="button" disabled={!selectedRow} onClick={() => setPrintModal({ open: true, row: selectedRow })}>
                  Print / View
                </button>
                <button className="btn primary" type="button" disabled={!selectedRow} onClick={() => window.print()}>
                  Print page
                </button>
              </div>
            </div>

            {selectedRow ? (
              <div style={{ marginTop: 12 }}>
                <PrintBlock row={selectedRow} />
              </div>
            ) : (
              <div className="muted" style={{ marginTop: 12 }}>Select a response on the left.</div>
            )}
          </div>
        </div>
      )}

      {/* Print modal */}
      {printModal.open && printModal.row ? (
        <div className="modal-overlay" onClick={() => setPrintModal({ open: false, row: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Survey Response</h3>
              <button className="btn" type="button" onClick={() => setPrintModal({ open: false, row: null })}>
                Close
              </button>
            </div>

            <PrintBlock row={printModal.row} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button className="btn primary" onClick={() => window.print()}>
                Print
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}